"use client"

import { HttpTypes } from "@medusajs/types"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { ChevronDown } from "lucide-react"
import { useState } from "react"
import type { CategoryTreeNode } from "@lib/config/category-tree"

type CategorySidebarProps = {
  category: HttpTypes.StoreProductCategory
  treeNode: CategoryTreeNode | null
  storeName: string
  siblingParent: CategoryTreeNode | null
  siblings: CategoryTreeNode[]
}

const CategorySidebar = ({ category, treeNode, storeName, siblingParent, siblings }: CategorySidebarProps) => {
  const [mobileOpen, setMobileOpen] = useState(false)

  const hasChildren = treeNode?.children && treeNode.children.length > 0

  let displayNodes: CategoryTreeNode[] = []
  let parentLabel: string | null = null
  let parentHandle: string | null = null

  if (hasChildren && treeNode) {
    // Current category has children in the tree → show them
    displayNodes = treeNode.children!
    parentLabel = treeNode.label
    parentHandle = treeNode.handle
  } else if (siblingParent && siblings.length > 0) {
    // Leaf category → show siblings from same parent
    displayNodes = siblings
    parentLabel = siblingParent.label
    parentHandle = siblingParent.handle
  }

  if (displayNodes.length === 0) return null

  const content = (
    <div className="flex flex-col gap-1">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
        Categories
      </h3>
      {parentLabel && parentHandle && (
        <LocalizedClientLink
          href={`/categories/${parentHandle}`}
          className="text-sm font-semibold text-gray-900 py-1.5 px-2 rounded bg-gray-100"
        >
          All {parentLabel}
        </LocalizedClientLink>
      )}
      {displayNodes.map((node) => (
        <LocalizedClientLink
          key={node.handle}
          href={`/categories/${node.handle}`}
          className={`text-sm py-1.5 px-2 pl-5 rounded transition-colors ${
            node.handle === category.handle
              ? "font-semibold text-gray-900 bg-gray-50"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
          }`}
        >
          {node.label}
        </LocalizedClientLink>
      ))}
    </div>
  )

  return (
    <>
      {/* Desktop */}
      <div className="hidden small:block py-4 mb-4 border-b border-gray-200">
        {content}
      </div>
      {/* Mobile: expandable */}
      <div className="small:hidden mb-4">
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="flex items-center justify-between w-full py-3 px-4 bg-gray-50 rounded-lg text-sm font-medium text-gray-700"
        >
          <span>Browse {parentLabel || category.name}</span>
          <ChevronDown
            size={16}
            className={`transition-transform duration-200 ${mobileOpen ? "rotate-180" : ""}`}
          />
        </button>
        {mobileOpen && (
          <div className="mt-2 px-2">
            {content}
          </div>
        )}
      </div>
    </>
  )
}

export default CategorySidebar
